import React, { useState, useEffect, useRef } from "react";
import Hls from "hls.js";
import io from "socket.io-client";
import { Send, Users, Wifi, WifiOff, MessageSquare } from "lucide-react";
import { useAuth } from "../../lib/auth"; // Pour récupérer le vrai utilisateur
import logoNms06 from "../../assets/logo_nms06.png";
import "../../styles/direct.css";

// Fonction pour couleur pseudo
function getUserColor(username) {
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }
  return "#" + ((hash >>> 0) & 0xffffff).toString(16).padStart(6, "0");
}

const Direct = () => {
  const { user } = useAuth(); // Récupération de l'utilisateur connecté
  const videoRef = useRef(null);
  const chatEndRef = useRef(null); // Pour scroller automatiquement en bas
  const socketRef = useRef(null); // Pour garder l'instance du socket

  const [liveStream, setLiveStream] = useState(null);
  const [chat, setChat] = useState([]);
  const [message, setMessage] = useState("");
  const [viewerCount, setViewerCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);

  // 1. Initialisation Socket.io
  useEffect(() => {
    // Connexion uniquement au montage
    socketRef.current = io("https://api.nms06.com/", {
      transports: ["websocket", "polling"],
      withCredentials: true,
    });

    socketRef.current.on("connect", () => setIsConnected(true));
    socketRef.current.on("disconnect", () => setIsConnected(false));

    // Écoute des messages
    socketRef.current.on("message", (payload) => {
      setChat((prev) => [...prev, payload]);
    });

    // Optionnel : Écoute du nombre de spectateurs (si ton backend le gère)
    socketRef.current.on("viewer_count", (count) => setViewerCount(count));

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, []);

  // 2. Scroll automatique vers le bas du chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat]);

  // 3. Récupération des infos du Live (API)
  useEffect(() => {
    const fetchLiveStream = async () => {
      try {
        const response = await fetch("https://api.nms06.com/api/live-stream/current");
        const contentType = response.headers.get("Content-Type");
        
        if (contentType && contentType.includes("application/json")) {
          const data = await response.json();
          // On vérifie si le live est actif (selon ta structure de données)
          if (data && (data.isActive || data.youtubeUrl || data.streamUrl)) {
             setLiveStream(data);
          } else {
             setLiveStream(null);
          }
        }
      } catch (error) {
        console.error("Erreur fetch live:", error);
      }
    };
    fetchLiveStream();
  }, []);

  // 4. Gestion HLS (Obs/Webcam)
  useEffect(() => {
    if (!liveStream || liveStream.type === "youtube") return;
    if (!liveStream.streamUrl || !videoRef.current) return;

    let hls;
    if (Hls.isSupported()) {
      hls = new Hls();
      hls.loadSource(liveStream.streamUrl);
      hls.attachMedia(videoRef.current);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        videoRef.current.play().catch((e) => console.log("Autoplay bloqué:", e));
      });
    } else if (videoRef.current.canPlayType("application/vnd.apple.mpegurl")) {
      videoRef.current.src = liveStream.streamUrl;
      videoRef.current.play();
    }

    return () => {
      if (hls) hls.destroy();
    };
  }, [liveStream]);

  // 5. Envoi message
  const sendMessage = (e) => {
    e?.preventDefault();
    if (!message.trim()) return;

    const payload = {
      message: message.trim(),
      // On utilise les vraies infos ou "Anonyme"
      username: user ? `${user.first_name} ${user.last_name}` : "Anonyme", 
      prenom: user?.first_name || "Anonyme",
      userId: user?.id || null
    };

    // Envoi optimiste (affichage immédiat) ou via socket uniquement
    // Ici on attend le retour du socket pour l'afficher à tout le monde
    socketRef.current.emit("message", payload);
    setMessage("");
  };

  return (
    <div className="direct-page">
      <div className="direct-container">
        
        {/* --- ZONE VIDÉO (Gauche / Haut) --- */}
        <div className="video-section">
          {liveStream ? (
            <div className="video-wrapper">
              {liveStream.type === "youtube" ? (
                <iframe
                  className="responsive-iframe"
                  src={`${liveStream.youtubeUrl}?autoplay=1&modestbranding=1`}
                  title="Direct NMS06"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              ) : (
                <video ref={videoRef} controls className="html5-video"></video>
              )}
              <div className="live-badge">
                <span className="pulsing-dot"></span> EN DIRECT
              </div>
            </div>
          ) : (
            <div className="no-stream">
              <img src={logoNms06} alt="NMS06 Offline" className="offline-logo" />
              <h3>Le direct est hors ligne</h3>
              <p>Retrouvez-nous très bientôt pour le prochain événement !</p>
            </div>
          )}

          {/* Barre d'infos sous la vidéo */}
          <div className="stream-info-bar">
            <div className="stream-status">
               {liveStream ? (
                 <span className="status-online"><Wifi size={18}/> En ligne</span>
               ) : (
                 <span className="status-offline"><WifiOff size={18}/> Hors ligne</span>
               )}
            </div>
            <div className="viewer-count">
              <Users size={18} /> {viewerCount} spectateurs
            </div>
          </div>
        </div>

        {/* --- ZONE CHAT (Droite / Bas) --- */}
        <div className="chat-section">
          <div className="chat-header">
            <MessageSquare size={20} /> Chat du direct
          </div>

          <div className="chat-window">
            {chat.length === 0 ? (
              <div className="chat-empty">
                <p>Soyez le premier à dire bonjour ! 👋</p>
              </div>
            ) : (
              chat.map((msg, index) => {
                const displayName = msg.prenom || (msg.username ? msg.username.split(" ")[0] : "Anonyme");
                return (
                  <div key={index} className="chat-message">
                    <span 
                      className="chat-username" 
                      style={{ color: getUserColor(displayName) }}
                    >
                      {displayName}:
                    </span>
                    <span className="chat-text">{msg.message || msg.text}</span>
                  </div>
                );
              })
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input Area */}
          <form className="chat-input-area" onSubmit={sendMessage}>
            {user ? (
              <>
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Participez à la discussion..."
                  className="chat-input"
                />
                <button type="submit" className="chat-send-btn">
                  <Send size={18} />
                </button>
              </>
            ) : (
              <div className="chat-login-prompt">
                <p>Connectez-vous pour chatter</p>
              </div>
            )}
          </form>
        </div>

      </div>
    </div>
  );
};

export default Direct;